import { openDB } from "idb";
import { DateTime } from "luxon";

class PanchangamDB {
  constructor() {
    this.dbName = "Sringeri_Panchangam";
    this.storeName = "panchangam";
    this.storeVersion = 4;
    this.db = null;
  }

  async init() {
    this.db = await openDB(this.dbName, this.storeVersion, {
      upgrade: (db, oldVersion, newVersion, transaction) => {
        let store;

        // v1-v3 logic (keep this so users on v0 get everything)
        if (oldVersion < 1) {
          store = db.createObjectStore(this.storeName, { keyPath: "date" });
        } else {
          store = transaction.objectStore(this.storeName);
        }

        if (oldVersion < 2) {
          store.createIndex("events_idx", "events", { multiEntry: true });
        }

        if (oldVersion < 3) {
          store.createIndex("tithi_vishesha_idx", "tithi_vishesha", {
            multiEntry: true,
          });
          store.createIndex("parva_vishesha_idx", "parva_vishesha", {
            multiEntry: true,
          });
          store.createIndex("samvatsara_idx", "samvatsara", {
            multiEntry: true,
          });
          store.createIndex("shaka_idx", "shaka", { multiEntry: true });
        }

        // v4: ADD THE MISSING DATE INDEX
        if (oldVersion < 4) {
          // We don't use multiEntry here because date is likely a single string
          store.createIndex("date", "date", { unique: false });
          console.log("Database upgraded to v4: added date index");
        }
      },
    });
  }

  // async init() {
  //   this.db = await openDB(this.dbName, this.storeVersion, {
  //     upgrade: (db, oldVersion, newVersion, transaction) => {
  //       let store;

  //       // v1: create object store
  //       if (oldVersion < 1) {
  //         store = db.createObjectStore(this.storeName, {
  //           keyPath: "date",
  //         });
  //       } else {
  //         store = transaction.objectStore(this.storeName);
  //       }

  //       // v2: add events index
  //       if (oldVersion < 2) {
  //         store.createIndex("events_idx", "events", {
  //           multiEntry: true,
  //         });
  //       }

  //       // v3: add vishesha indexes
  //       if (oldVersion < 3) {
  //         store.createIndex("tithi_vishesha_idx", "tithi_vishesha", {
  //           multiEntry: true,
  //         });

  //         store.createIndex("parva_vishesha_idx", "parva_vishesha", {
  //           multiEntry: true,
  //         });

  //         store.createIndex("samvatsara_idx", "samvatsara", {
  //           multiEntry: true,
  //         });

  //         store.createIndex("shaka_idx", "shaka", {
  //           multiEntry: true,
  //         });
  //       }
  //     },
  //   });
  // }

  // Helper to get transaction store
  _getStore(mode = "readonly") {
    return this.db.transaction(this.storeName, mode).store;
  }

  // Find by date
  async getDay(dateKey) {
    if (!this.db || !dateKey) return null;
    return await this._getStore().get(dateKey);
  }

  async getPresentShakaAndSamvatsara() {
    if (!this.db) throw new Error("DB not initialized");

    // YYYY-MM-DD in IST
    let today = DateTime.now().setZone("Asia/Kolkata");

    let { min, max } = await this.getBounds();

    console.log(typeof min, min, typeof max, max); //, max);

    max = DateTime.fromFormat(max, "yyyy-LL-dd").setZone("Asia/Kolkata"); // YYYY-mm-dd
    min = DateTime.fromFormat(min, "yyyy-LL-dd").setZone("Asia/Kolkata");

    console.log(min);
    if (today < min) today = min;
    if (today > max) today = max;

    today = today.toFormat("yyyy-LL-dd");

    console.log(today);

    const record = await this.getDay(today);

    console.log(record);
    if (!record) return null;

    return {
      shaka: record.shaka,
      samvatsara: record.samvatsara,
    };
  }

  async sync(jsonArray) {
  if (!this.db) throw new Error("DB not initialized");

  // Flatten the data if it's nested [[day, day], [day, day]]
  const allDays = Array.isArray(jsonArray.data[0]) 
    ? jsonArray.data.flat() 
    : jsonArray.data;

  // 1. Create a single transaction for the whole operation
  const tx = this.db.transaction(this.storeName, "readwrite");
  const store = tx.store;

  try {
    // 2. Fire off all 'put' requests without awaiting each one individually.
    // This keeps the transaction active in the event loop.
    for (const day of allDays) {
      store.put(day); 
    }

    // 3. Await the transaction's 'done' promise to ensure all writes finished.
    await tx.done;

    // 4. Update metadata only after successful DB commit.
    localStorage.setItem("panchangamMeta", JSON.stringify(jsonArray.meta));
    console.log("Sync successful");
    return true;
  } catch (err) {
    console.error("Sync failed, transaction aborted:", err);
    // In idb, tx.done will reject if any request fails, 
    // and the transaction will automatically abort.
    return false;
  }
}


  // Bulk Load from the JSON file you generated via PHP
  async _sync(jsonArray) {
    if (!this.db) throw new Error("DB not initialized");

    console.log(typeof jsonArray, jsonArray);

    const tx = this.db.transaction(this.storeName, "readwrite");
    const store = tx.store;

    // Batch insertion to avoid blocking and memory issues
    const batchSize = 500;

    const data = jsonArray.data;

    for (let i = 0; i < data.length; i += batchSize) {
      const batch = data.slice(i, i + batchSize);
      await Promise.all(batch.map((item) => store.put(item)));
    }

    await tx.done;

    localStorage.setItem("panchangamMeta", JSON.stringify(jsonArray.meta));
    return true;
  }

  // Check if database needs sync (basic heuristic)
  async needsSync(fromDate) {
    // first check if same year panchangam is updated or not
    let localMeta = localStorage.getItem("panchangamMeta");

    if (!localMeta) {
      return true; // First Run
    }

    localMeta = JSON.parse(localMeta);

    // 1. YEAR TRANSITION CHECK (Crucial)
    // If today is beyond the end of the current stored year, we must sync.
    if (fromDate > localMeta.end_date) return true;

    // 2. Derive filename (Fixing the year extraction)
    // Assuming startDate is "2026-03-19", this gets "2026_2027.json"
    const startYear = localMeta.start_date.substring(0, 4);
    const endYear = localMeta.end_date.substring(0, 4);
    const panchangamFile = `${startYear}_${endYear}.json`;

    try {
      const response = await fetch(`/res/panchangam/${panchangamFile}`);
      const remotePanchangam = await response.json();

      // 3. THE CHECK: Compare the Unix timestamps
      // If the remote file's modified time is greater, we need an update
      if (
        parseInt(remotePanchangam.meta.modified) > parseInt(localMeta.modified)
      ) {
        return true;
      }
    } catch (e) {
      console.error(e);
      return false;
    }

    if (!this.db) throw new Error("DB not initialized");

    if (await this.isEmpty()) return true;

    const range = IDBKeyRange.lowerBound(fromDate);
    const count = await this._getStore().count(range);

    // If less than 15 records after fromDate, sync
    return count <= 15;
  }

  // Returns first and last date available in DB
  async getBounds() {
    if (!this.db) throw new Error("DB not initialized");

    const store = this._getStore();
    const firstCursor = await store.openCursor();
    const lastCursor = await store.openCursor(null, "prev");

    return {
      min: firstCursor ? firstCursor.key : null,
      max: lastCursor ? lastCursor.key : null,
    };
  }

  // Check if store is empty
  async isEmpty() {
    if (!this.db) throw new Error("DB not initialized");
    const count = await this._getStore().count();
    return count === 0;
  }

  async getDocumentsWithVishesha(fieldName, limit = 200) {
    if (!this.db) throw new Error("DB not initialized");

    const validFields = ["tithi_vishesha", "parva_vishesha"];
    if (!validFields.includes(fieldName)) {
      throw new Error(`Invalid field name: ${fieldName}`);
    }

    const shakaSamvatsara = await this.getPresentShakaAndSamvatsara();
    if (!shakaSamvatsara) return;
    const { shaka, samvatsara } = shakaSamvatsara;

    // FIX: Open the cursor on the 'date' index to ensure chronological order
    const index = this._getStore().index("date");
    const results = [];
    let cursor = await index.openCursor(); // This will now walk in date order

    while (cursor && results.length < limit) {
      const record = cursor.value;

      // Check if current year matches AND the requested field has data
      const hasVishesha = record[fieldName] && record[fieldName].length > 0;

      if (
        record.shaka === shaka &&
        record.samvatsara === samvatsara &&
        hasVishesha
      ) {
        results.push(record);
      }

      cursor = await cursor.continue();
    }

    return results;
  }

  // Query vishesha fields with optional limit
  async _getDocumentsWithVishesha(fieldName, limit = 200) {
    if (!this.db) throw new Error("DB not initialized");

    const validFields = ["tithi_vishesha", "parva_vishesha"];
    if (!validFields.includes(fieldName)) {
      throw new Error(
        `Invalid field name: ${fieldName}. Valid options are: ${validFields.join(
          ", ",
        )}`,
      );
    }

    const shakaSamvatsara = await this.getPresentShakaAndSamvatsara();

    if (!shakaSamvatsara) return;

    const { shaka, samvatsara } = shakaSamvatsara;

    const indexName = `${fieldName}_idx`;
    const index = this._getStore().index(indexName);

    const results = [];
    let cursor = await index.openCursor();

    while (cursor && results.length < limit) {
      const record = cursor.value;

      if (record.shaka === shaka && record.samvatsara === samvatsara) {
        results.push(record);
      }

      cursor = await cursor.continue();
    }

    return results;
  }

  async clearOlderThan(days = 400) {
    if (!this.db) throw new Error("DB not initialized");

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);

    // Use local YYYY-MM-DD
    const cutoffKey = cutoff.toLocaleDateString("en-CA");

    const tx = this.db.transaction(this.storeName, "readwrite");
    const store = tx.store;

    let cursor = await store.openCursor();
    while (cursor) {
      if (cursor.key < cutoffKey) {
        await cursor.delete();
      }
      cursor = await cursor.continue();
    }

    await tx.done;
    return true;
  }
}

export const dbService = new PanchangamDB();
